import { format, isToday } from 'date-fns'

const STATE_STYLES = {
  empty: 'meal-empty',
  proposed: 'meal-proposed',
  countered: 'meal-countered',
  agreed: 'meal-agreed',
}

const STATE_BADGES = {
  proposed: { label: 'Pending', color: 'bg-amber-200 text-amber-800' },
  countered: { label: 'Counter', color: 'bg-orange-200 text-orange-800' },
  agreed: { label: 'Agreed ✓', color: 'bg-green-200 text-green-800' },
}

const MEAL_ICONS = {
  manual: '🍳',
  imported: '🌐',
  restaurant: '🥡',
}

export default function CalendarDay({ date, entry, meal, currentUser, onAdd, onClick }) {
  const dateStr = format(date, 'yyyy-MM-dd')
  const state = entry?.state || 'empty'
  const isReadOnly = currentUser?.role === 'readonly'

  // Check if current user needs to act (has a pending counter to review)
  const needsAttention = (
    (state === 'proposed' && entry.proposal?.proposedBy !== currentUser?.userId) ||
    (state === 'countered' && entry.counter?.proposedBy !== currentUser?.userId)
  )

  return (
    <div
      className={`rounded-xl p-3 transition-all ${STATE_STYLES[state]} ${
        state !== 'empty' ? 'cursor-pointer hover:shadow-md active:scale-98' : ''
      }`}
      onClick={state !== 'empty' ? onClick : undefined}
    >
      <div className="flex items-start justify-between">
        {/* Date label */}
        <div className="flex items-center gap-2">
          <div className={`text-sm font-semibold ${isToday(date) ? 'text-green-700' : 'text-gray-600'}`}>
            {format(date, 'EEE')}
            {isToday(date) && <span className="ml-1 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full">Today</span>}
          </div>
          <div className="text-sm text-gray-400">{format(date, 'MMM d')}</div>
        </div>

        {/* State badge */}
        {STATE_BADGES[state] && (
          <div className="flex items-center gap-1">
            {needsAttention && (
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" title="Needs your input" />
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATE_BADGES[state].color}`}>
              {STATE_BADGES[state].label}
            </span>
          </div>
        )}
      </div>

      {/* Meal content */}
      <div className="mt-2">
        {state === 'empty' ? (
          !isReadOnly ? (
            <button
              onClick={e => { e.stopPropagation(); onAdd() }}
              className="w-full py-2 text-sm text-gray-400 hover:text-green-600 flex items-center justify-center gap-1 transition-colors"
            >
              <span className="text-lg">+</span> Add dinner
            </button>
          ) : (
            <p className="text-sm text-gray-400 text-center py-2">No dinner planned</p>
          )
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg">{MEAL_ICONS[meal?.type] || '🍽️'}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{meal?.name || 'Unknown meal'}</p>
              {state === 'countered' && (
                <p className="text-xs text-orange-600 mt-0.5">Counter-proposal pending</p>
              )}
              {entry?.proposal?.dayNotes && state !== 'agreed' && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{entry.proposal.dayNotes}</p>
              )}
              {entry?.agreedDayNotes && state === 'agreed' && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{entry.agreedDayNotes}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
